import {Global, css} from '@emotion/react'
import React, {useState, useEffect, useMemo, useCallback, useId, useRef} from 'react'
import loadable from '@loadable/component'
import {useIntl} from 'react-intl'
import {useGoogleMapsApi, isGoogleMapsLibraryLoaded} from '@app/components/google-maps'
import {useForm} from 'react-hook-form'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import MapAccordion from './stores-map-accordion'
import useShipToCountry from '@app/hooks/use-ship-to-country'

import usePaginatedSearchStores from '../../hooks/use-paginated-search-stores'
import useSiteType from '../../hooks/use-site-type'

// Utils
import {isServer} from '@app/utils/utils'

import {
    Box,
    BreadcrumbItem as ChakraBreadcrumbItem,
    BreadcrumbLink as ChakraBreadcrumbLink,
    Button,
    Heading,
    InputRightElement,
    List,
    Stack,
    Text,
    useDisclosure,
    useBreakpointValue,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Breadcrumb from '@app/components/breadcrumb'

import Seo from '@salesforce/retail-react-app/app/components/seo'
import Field from '../../components/field'
import FieldsetForm from '../../components/shared/ui/FieldsetForm'
import {
    FilterIcon,
    StoreOthersIcon,
    StoreOutletIcon,
    StoreVBQIcon
} from '../../components/custom-icons'
import StoreCard from './store-card'
import StoresFilters from './stores-filters'
import {haversineDistance, getClientDefaultLocation} from '../../utils/utils'
import useSearchStoreField from '../../components/forms/useSearchStoreFields'
import PropTypes from 'prop-types'
const CheckoutStoreCard = loadable(() => import('./checkout-store-card'))

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'
import GooglePlacesSuggestions from './places-suggestions'
import StoreMap from './store-map'

/**
 * GoogleMapComponent is a React functional component that integrates Google Maps to display
 * store locations and allows users to search and filter stores based on various criteria.
 *
 * This component utilizes several hooks and APIs, including the Google Maps JavaScript API,
 * to provide a dynamic and interactive map experience. It supports geolocation to center the
 * map on the user's current location and offers filtering options to refine the list of displayed
 * stores. The component is designed to be responsive and adapts its layout based on the screen size.
 *
 * @param {Object} props - The component properties.
 * @param {boolean} [props.isCheckout=false] - Indicates if the component is used in a checkout context.
 * @param {Function} props.onClose - Callback function to handle closing actions.
 * @param {Array|null} [props.checkoutStores=null] - An optional array of stores to display during checkout.
 *
 * @returns {JSX.Element} The rendered GoogleMapComponent.
 */
const GoogleMapComponent = ({isCheckout = false, onClose, checkoutStores = null}) => {
    const {app: appConfig} = getConfig()
    const {isUS} = useSiteType()
    const CLIENT_DEFAULT_LOCATION = getClientDefaultLocation(isUS)
    const [location, setLocation] = useState(CLIENT_DEFAULT_LOCATION)
    const {formatMessage} = useIntl()
    const googleInstructionsId = useId()
    const googleResultsId = `${googleInstructionsId}-results`
    const [isGoogleSuggestionsOpen, setIsGoogleSuggestionsOpen] = useState(false)
    const [activeGoogleSuggestionId, setActiveGoogleSuggestionId] = useState(null)
    const storeSearchInputRef = useRef(null)
    const form = useForm()
    const {setValue} = form

    const [stores, setStores] = useState(checkoutStores ?? [])
    const [filteredStores, setFilteredStores] = useState([])
    const [filters, setFilters] = useState({})
    const [inputValue, setInputValue] = useState('')
    const [searchTypes, setSearchTypes] = useState(null)

    const isMobile = useBreakpointValue({
        base: true,
        md: false
    })

    const {triggerClickStoreFiltersEvent} = useGTM()

    const libraries = useMemo(() => ['places', 'marker'], [])
    const {isApiAndLibrariesLoaded: isGoogleMapsApiLoaded} = useGoogleMapsApi({
        libraries: libraries
    })
    const {shipToCountry} = useShipToCountry()

    /**
     * Attempts to retrieve the user's current geographical location using the browser's Geolocation API.
     *
     * If the Geolocation API is available, it requests the user's current position and calls the
     * appropriate success or error callback. If the API is not available, it defaults the location
     * to a predefined constant.
     *
     * @function
     * @returns {void}
     */
    const getUserGeolocation = () => {
        if (typeof navigator !== 'undefined' && navigator.permissions && navigator.geolocation) {
            navigator.permissions
                .query({name: 'geolocation'})
                .then((result) => {
                    if (result.state === 'granted' || result.state === 'prompt') {
                        navigator.geolocation.getCurrentPosition(
                            getGeolocationSuccess,
                            getGeolocationError
                        )
                    } else if (result.state === 'denied') {
                        console.error('Geolocation permission denied.')
                        setLocation(CLIENT_DEFAULT_LOCATION)
                    }

                    // Listen for changes in permission state
                    result.onchange = () => {
                        if (result.state === 'granted') {
                            navigator.geolocation.getCurrentPosition(
                                getGeolocationSuccess,
                                getGeolocationError
                            )
                        } else if (result.state === 'denied') {
                            console.error('Geolocation permission denied.')
                            setLocation(CLIENT_DEFAULT_LOCATION)
                        }
                    }
                })
                .catch((error) => {
                    console.error('Error querying geolocation permissions:', error)
                    setLocation(CLIENT_DEFAULT_LOCATION)
                })
        } else {
            console.warn('Geolocation is not supported by this browser.')
            setLocation(CLIENT_DEFAULT_LOCATION)
        }
    }

    const getGeolocationSuccess = (position) => {
        setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
        })
    }

    const getGeolocationError = (error) => {
        console.error(`Geolocation error (${error.code}): ${error.message}`)
        setLocation(CLIENT_DEFAULT_LOCATION)
    }

    useEffect(() => {
        getUserGeolocation()
    }, [isGoogleMapsApiLoaded])

    /**
     * Handles the change event for checkboxes, updating the filter state accordingly.
     *
     * This function updates the `filters` state by toggling the boolean value of the checkbox
     * that triggered the event. It uses the checkbox's `name` attribute to identify which filter
     * to update.
     *
     * @param {Object} event - The event object from the checkbox change.
     * @param {HTMLInputElement} event.target - The target element of the event.
     * @param {string} event.target.name - The name attribute of the checkbox, used to identify the filter.
     * @param {boolean} event.target.checked - The checked state of the checkbox.
     */
    const handleCheckboxChange = (event) => {
        const {name, checked} = event.target
        setFilters((prevFilters) => ({
            ...prevFilters,
            [name]: checked
        }))
    }

    /**
     * Retrieves a paginated list of stores to display based on the current site ID and user location.
     *
     * Utilizes the `usePaginatedSearchStores` hook to fetch store data, passing the site's unique identifier
     * and the user's geographic coordinates (latitude and longitude). The query is conditionally enabled
     * depending on the `checkoutStores` flag. The hook returns the fetched store data as `storesToDisplay`
     * and a loading state as `isLoading`.
     *
     * @constant
     * @type {Object}
     * @property {Array} data - The list of stores to display.
     * @property {boolean} isLoading - Indicates whether the store data is currently being loaded.
     */
    const {data: storesToDisplay, isLoading} = usePaginatedSearchStores(
        {
            siteId: appConfig.commerceAPI.parameters.siteId,
            longitude: location.latitude,
            latitude: location.longitude,
            c_country: shipToCountry?.id
        },
        {enabled: !checkoutStores}
    )

    /**
     * Updates the store list.
     *
     * This effect runs once when the component mounts.
     * - Updates the store state with the data available in `storesToDisplay`.
     */
    useEffect(() => {
        if (storesToDisplay && !checkoutStores) setStores(storesToDisplay)
    }, [isLoading])

    /**
     * Filters and memorize the list of stores based on the selected filters.
     *
     * This effect runs whenever the stores or filters change. It filters the list of stores
     * based on the specified collections, types, and services in the filters. If no filters
     * are active, it returns all stores.
     *
     * @param {Array} stores.data - The list of stores to filter.
     * @param {Object} filters - The filters applied to the stores.
     * @param {boolean} filters.men - Filter for "Men" collections.
     * @param {boolean} filters.women - Filter for "Women" collections.
     * @param {boolean} filters.kids - Filter for "Kids" collections.
     * @param {boolean} filters.VS - Filter for store type "VS".
     * @param {boolean} filters.VO - Filter for store type "VO".
     * @param {boolean} filters.OS - Filter for store type "OS".
     * @param {boolean} filters.monogramming - Filter for stores offering "Monogramming" services.
     */
    const filteredStoresMemo = useMemo(() => {
        // remove closed store and stores that have flag "hide on store locator" as true
        const openedStores = stores?.data?.filter(
            (store) =>
                !store.c_hideOnStorelocator && new Date(store.c_closingDate).getTime() > Date.now()
        )

        if (Object.values(filters).some((value) => value === true)) {
            return openedStores.filter((store) => {
                // Collections (OR logic within the category)
                const matchesCollections =
                    ['Men', 'Women', 'Kids'].some(
                        (collection) =>
                            filters[collection.toLowerCase()] &&
                            store.c_collections?.includes(collection)
                    ) || !['men', 'women', 'kids'].some((collection) => filters[collection])

                // Types (OR logic within the category)
                const matchesTypes =
                    ['VS', 'VO', 'OS'].some(
                        (type) => filters[type] && store.c_storeType === type
                    ) || !['VS', 'VO', 'OS'].some((type) => filters[type])

                // Services (OR logic within the category)
                const matchesServices = filters.monogramming
                    ? store.c_services?.includes('Monogramming')
                    : true

                // Combine all filters with AND logic between categories
                return matchesCollections && matchesTypes && matchesServices
            })
        } else {
            return openedStores
        }
    }, [stores, filters])

    useEffect(() => {
        setFilteredStores(filteredStoresMemo)
    }, [stores, filters])

    const fetchPlaceLocation = async (placeId) => {
        if (!placeId || !isGoogleMapsLibraryLoaded(['places'])) {
            throw new Error('Place API is not initialized or placeId is missing.')
        }

        try {
            const place = new window.google.maps.places.Place({
                id: placeId
            })

            await place.fetchFields({
                fields: ['displayName', 'formattedAddress', 'location']
            })

            if (place.location) {
                return {
                    latitude: place.location.lat(),
                    longitude: place.location.lng()
                }
            } else {
                throw new Error('No location data found for the place.')
            }
        } catch (error) {
            console.error('Error fetching place details:', error)
            throw error
        }
    }

    const handleSuggestionClick = async (suggestion) => {
        const placePrediction = suggestion.placePrediction
        const placeId = placePrediction.placeId

        setValue('store_search_field', placePrediction.text.toString())
        triggerClickStoreFiltersEvent(placePrediction.text.toString(), filters)

        try {
            const location = await fetchPlaceLocation(placeId)
            setLocation(location)

            setSearchTypes(placePrediction.types)
        } catch (error) {
            console.error(error)
        }
    }

    /**
     * Handles the store search by using the Google Places API to get place predictions
     * and updating the location based on the first prediction's details.
     *
     * @param {Object} data - The form data containing the search input.
     * @param {string} data.store_search_field - The search input value used for place predictions.
     */
    const handleStoreSearch = async (data) => {
        const input = data.store_search_field

        if (isGoogleMapsLibraryLoaded(['places']) && input) {
            const request = {
                input,
                sessionToken: new window.google.maps.places.AutocompleteSessionToken()
            }

            try {
                const {suggestions} =
                    await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                        request
                    )

                if (suggestions && suggestions.length > 0) {
                    const firstPrediction = suggestions[0].placePrediction

                    setValue('store_search_field', firstPrediction.text.toString())

                    try {
                        const location = await fetchPlaceLocation(firstPrediction.placeId)
                        setLocation(location)

                        setSearchTypes(firstPrediction.types)
                    } catch (error) {
                        console.error(error)
                    }
                }
            } catch (error) {
                console.error('Error fetching place predictions:', error)
            }
        }
    }

    const searchStoreField = useSearchStoreField({form, prefix: 'store_search'})

    const storeTypes = [
        {
            icon: StoreVBQIcon,
            label: formatMessage({
                id: 'stores_page.store_vbq',
                defaultMessage: 'Vilebrequin stores'
            })
        },
        {
            icon: StoreOutletIcon,
            label: formatMessage({
                id: 'stores_page.store_outlet',
                defaultMessage: 'Outlet stores'
            })
        },
        {
            icon: StoreOthersIcon,
            label: formatMessage({
                id: 'stores_page.store_others',
                defaultMessage: 'Others stores'
            })
        }
    ]

    const {isOpen: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose} = useDisclosure()

    const handleInputChange = useCallback((inputValue) => {
        setInputValue(inputValue)
        setActiveGoogleSuggestionId(null)
    }, [])

    return (
        <Box w="full">
            <GlobalStyles />
            {!isCheckout && (
                <Breadcrumb type="stores">
                    <ChakraBreadcrumbItem isCurrentPage>
                        <ChakraBreadcrumbLink color="fullBlack">
                            {formatMessage({
                                id: 'stores_page.link',
                                defaultMessage: 'Stores'
                            })}
                        </ChakraBreadcrumbLink>
                    </ChakraBreadcrumbItem>
                </Breadcrumb>
            )}
            <Box
                data-testid="stores-page"
                padding={{base: '24px 0', lg: isCheckout ? '0' : '32px 40px'}}
            >
                {!isCheckout && (
                    <Seo
                        title={formatMessage({
                            id: 'stores_page.seo.title',
                            defaultMessage: 'Stores'
                        })}
                        description={formatMessage({
                            id: 'stores_page.seo.desc',
                            defaultMessage: 'Find a store'
                        })}
                    />
                )}

                <Box px={{base: isCheckout ? '0' : '10px', lg: 'unset'}}>
                    <Heading
                        as="h1"
                        mb="16px"
                        textTransform={'uppercase'}
                        fontSize={isCheckout ? {base: '3xl', lg: '4xl'} : '4xl'}
                    >
                        {isCheckout
                            ? formatMessage({
                                  id: 'checkout_click_and_collect.title',
                                  defaultMessage: 'Collect from a Vilebrequin store'
                              })
                            : formatMessage({
                                  id: 'stores_page.title',
                                  defaultMessage: 'Vilebrequin stores, outlet & official resellers'
                              })}
                    </Heading>

                    <Text mb={'16px'} variant={'bodyBase2'} fontSize={'13px'}>
                        {formatMessage({
                            id: 'stores_page.description',
                            defaultMessage:
                                'Collect your products free of charge from the nearest Vilebrequin store.'
                        })}
                    </Text>

                    {isGoogleMapsApiLoaded && (
                        <>
                            <Stack direction={'row'} position={'relative'} mb={'16px'}>
                                <Box
                                    width={'100%'}
                                    maxWidth={{base: 'unset', lg: isCheckout ? '31%' : '556px'}}
                                >
                                    <FieldsetForm
                                        id="search-store-form"
                                        legendText={formatMessage({
                                            defaultMessage: 'Search store form',
                                            id: 'store_search.form.legend'
                                        })}
                                        onSubmit={(e) => e.preventDefault()}
                                        error={form.formState.errors?.global?.message}
                                        isMandatoryNote={false}
                                        style={{width: '100%'}}
                                    >
                                        <Box>
                                            <Field
                                                {...searchStoreField.search}
                                                hasGroupRole
                                                autoComplete="off"
                                                inputProps={{
                                                    ...searchStoreField.search.inputProps,
                                                    'aria-autocomplete': 'list',
                                                    'aria-controls': googleResultsId,
                                                    'aria-haspopup': 'listbox',
                                                    'aria-expanded': isGoogleSuggestionsOpen,
                                                    'aria-describedby': googleInstructionsId,
                                                    'aria-activedescendant':
                                                        activeGoogleSuggestionId || undefined
                                                }}
                                                inputRef={storeSearchInputRef}
                                                customProps={{
                                                    onChange: handleInputChange
                                                }}
                                            >
                                                <InputRightElement w={'auto'} mx={0}>
                                                    <Button
                                                        type="submit"
                                                        variant={'primary'}
                                                        textTransform={'uppercase'}
                                                        onClick={form.handleSubmit(
                                                            handleStoreSearch
                                                        )}
                                                    >
                                                        {formatMessage({
                                                            id: 'stores_page.submit',
                                                            defaultMessage: 'Search'
                                                        })}
                                                    </Button>
                                                </InputRightElement>
                                            </Field>

                                            <GooglePlacesSuggestions
                                                input={inputValue}
                                                onSuggestionClick={handleSuggestionClick}
                                                instructionsId={googleInstructionsId}
                                                resultsId={googleResultsId}
                                                onOpenChange={setIsGoogleSuggestionsOpen}
                                                inputRef={storeSearchInputRef}
                                                onActiveDescendantChange={
                                                    setActiveGoogleSuggestionId
                                                }
                                            />
                                        </Box>
                                    </FieldsetForm>
                                </Box>

                                {!isCheckout && (
                                    <>
                                        <Button
                                            aria-label={formatMessage({
                                                id: 'stores_page.filter',
                                                defaultMessage: 'Open filters'
                                            })}
                                            onClick={() => onFiltersOpen(true)}
                                            position={{base: 'absolute', lg: 'static'}}
                                            bottom={'-16px'}
                                            px={0}
                                            ml={'32px'}
                                            right={0}
                                            top={'calc(100% + 16px)'}
                                        >
                                            <FilterIcon />
                                        </Button>

                                        <StoresFilters
                                            handleCheckboxChange={handleCheckboxChange}
                                            filters={filters}
                                            storesNumber={filteredStores?.length}
                                            clearFilters={() => setFilters({})}
                                            isFiltersOpen={isFiltersOpen}
                                            onFiltersClose={onFiltersClose}
                                        />
                                    </>
                                )}
                            </Stack>

                            {isCheckout ? (
                                <Heading
                                    variant={'h3'}
                                    mt="30px"
                                    mb="10px"
                                    textTransform={'uppercase'}
                                    fontSize={'2xl'}
                                >
                                    {formatMessage({
                                        id: 'checkout_click_and_collect.select_a_store',
                                        defaultMessage: 'Select a store'
                                    })}
                                </Heading>
                            ) : (
                                <Stack
                                    direction={{base: 'column', lg: 'row'}}
                                    mb={'24px'}
                                    gap={{base: '8px', lg: '40px'}}
                                >
                                    {storeTypes.map(({icon: Icon, label}) => (
                                        <Text
                                            key={label}
                                            textTransform={'uppercase'}
                                            fontSize={'12px'}
                                        >
                                            <Icon mr={'8px'} />
                                            {label}
                                        </Text>
                                    ))}
                                </Stack>
                            )}
                        </>
                    )}
                </Box>

                {isGoogleMapsApiLoaded ? (
                    <Stack
                        direction={{base: 'column', lg: 'row'}}
                        gap={{base: 'unset', lg: '20px'}}
                    >
                        <Box
                            flex={isCheckout && !isMobile ? '2' : 'unset'}
                            width={'100%'}
                            height={isCheckout && isMobile ? 'auto' : '602px'}
                            maxHeight={{base: isCheckout ? '600px' : '414px', lg: 'unset'}}
                        >
                            <MapAccordion isCheckout={isCheckout} isMobile={isMobile}>
                                <StoreMap
                                    isApiLoaded={isGoogleMapsApiLoaded}
                                    filteredStores={filteredStores}
                                    location={location}
                                    stores={stores}
                                    isDeliveryCountryBonds={checkoutStores}
                                    searchTypes={searchTypes}
                                />
                            </MapAccordion>
                        </Box>

                        <List
                            overflowY="auto"
                            flex={
                                isCheckout && !isMobile
                                    ? {base: 'unset', lg: '1'}
                                    : {base: 'unset', lg: '0 0 50%'}
                            }
                            padding={{base: isCheckout ? '0' : '0 16px', lg: 'unset'}}
                            maxHeight={{base: 'unset', lg: '602px'}}
                        >
                            {filteredStores
                                ?.sort(
                                    (store1, store2) =>
                                        haversineDistance(
                                            location.latitude,
                                            location.longitude,
                                            store1.latitude,
                                            store1.longitude
                                        ) -
                                        haversineDistance(
                                            location.latitude,
                                            location.longitude,
                                            store2.latitude,
                                            store2.longitude
                                        )
                                )
                                .map((store) =>
                                    isCheckout ? (
                                        <CheckoutStoreCard
                                            key={store.id}
                                            store={store}
                                            onClose={onClose}
                                        />
                                    ) : (
                                        <StoreCard key={store.id} store={store} />
                                    )
                                )}
                        </List>
                    </Stack>
                ) : !isServer ? (
                    <Skeleton height={'100dvh'} width={'100%'} />
                ) : null}
            </Box>
        </Box>
    )
}

/**
 * A React functional component that applies global styles using Emotion's Global component.
 * This component specifically sets the z-index for elements with the class 'pac-container'.
 *
 * @returns {JSX.Element} A JSX element that applies the specified global styles.
 */
const GlobalStyles = () => (
    <Global
        styles={css`
            .pac-container {
                z-index: 1401;
            }
        `}
    />
)

export default GoogleMapComponent

GoogleMapComponent.propTypes = {
    isCheckout: PropTypes.bool,
    onClose: PropTypes.func,
    checkoutStores: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])])
}
